import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder"
import { equipmentStatusTranslation } from "@/common/constants/equipmentStatus.constant"
import { formatDateTime } from "@/common/utils/formatters"
import { CompanyEquipmentData } from "@/features/privateCompanies/types/companyEquipments.type"
import { CarFrontIcon, CarIcon } from "lucide-react"
import { Link } from "react-router-dom"

interface CompanyEquipmentsGridItemProps {
  companyId?: string
  equipment: CompanyEquipmentData
}

export const CompanyEquipmentsGridItem = ({ companyId, equipment }: CompanyEquipmentsGridItemProps) => {
  const showPendingCard = equipment?.card?.status !== 'shipped' && equipment?.card?.status !== 'active'
  const cardToDisplay = showPendingCard ? equipment?.pending_card || equipment?.card : equipment.card;

  const categoryFieldForDisplayName = equipment?.model?.category?.field_for_display;
  const displayName = (categoryFieldForDisplayName && equipment[categoryFieldForDisplayName as keyof typeof equipment] as string) ||
    equipment.license_plate ||
    equipment.official_name ||
    equipment.serial_number ||
    'Não informado';

  return (
    <Link to={`/companies/${companyId}/equipments/${equipment.id}`} className="flex flex-col bg-white-200 rounded-lg overflow-hidden">
      <ImageWithPlaceholder
        url={equipment.photos?.length ? equipment.photos[0]?.url : undefined}
        alt={equipment.model?.display_name}
        placeholderIcon={CarIcon}
        className="w-full size-auto rounded-none bg-white-800 aspect-[4/3]"
        iconClassName="size-32"
      />
      <div className="w-full space-y-2 text-white-800 p-4">
        <div className="flex">
          <div className="p-1 bg-yellow-op-2 rounded-sm flex items-center gap-1.5">
            {categoryFieldForDisplayName === 'license_plate' && (
              <CarFrontIcon strokeWidth={1.5} size={18} />
            )}

            <p data-testid="equipment_display_name" className="font-bold text-base">
              {displayName}
            </p>
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-xs">{equipment.model?.brand || 'Não informado'}</p>
          <p className="text-xs">{equipment.model?.display_name} {equipment.model?.year}</p>
        </div>
        <div className="flex gap-x-2">
          <div className="space-y-1">
            <p className="font-light text-xs italic">Status</p>
            <p className="text-[11px] leading-3">{equipmentStatusTranslation[(equipment.status) as keyof typeof equipmentStatusTranslation]}</p>
          </div>
          <div className="space-y-1">
            <p className="font-light text-xs italic">Filial</p>
            <p data-testid="equipment_branch_display_name" className="text-[11px] leading-3">{equipment.branch.display_name}</p>
          </div>
        </div>
        <div className="space-y-1">
          <p className="font-light text-xs italic">Cartão</p>
          <p className="text-[11px] leading-3">**** **** **** {cardToDisplay?.last_four_digits}</p>
        </div>
        <div className="space-y-1">
          <p className="font-light text-xs italic">{equipment.model?.category?.unit_for_consumption === 'hours' ? 'Horas' : 'Quilometragem'}</p>
          <p className="text-[11px] leading-3">{equipment.last_usage_value}{equipment.model?.category?.unit_for_consumption === 'hours' ? ' h' : ' km'} {equipment.last_usage_at && <span className="font-light italic">{formatDateTime(new Date(equipment.last_usage_at))}</span>}</p>
        </div>
        <div className="space-y-1">
          <p data-testid="last_consumption" className="font-light text-xs italic">Último consumo</p>
          <p data-testid="last_consumption_created_at" className="text-[11px] leading-3">{equipment?.last_consumption?.created_at ? formatDateTime(new Date(equipment?.last_consumption?.created_at)) : '--'}</p>
        </div>
        <div className="space-y-1">
          <p className='text-[8px] italic font-light'>{`Cadastrado em ${formatDateTime(new Date(equipment.created_at || 0))}`}</p>
        </div>
      </div>
    </Link>
  )
}
